// /src/PrivateRoute.jsx

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './authContext';
import { useSecurity } from './SecurityContext';
import { useStatus } from './StatusContext';
import LoadingSpinner from './components/LoadingSpinner';
import UnderMaintenance from './pages/UnderMaintenance';
import { useWebsiteAccessGate } from './hooks/useWebsiteAccessGate';
import { logOnboardingGateDecision } from './utils/onboardingGateLog';

const ONBOARDING_PREFIX = '/members/onboarding';

function PrivateRoute({ children }) {
  const { isLoggedIn, isLoading, userId } = useAuth();
  const { loading: securityLoading, viewAsUserId, isSuperAdmin, isViewAsActive, viewAsLoading } = useSecurity();
  const { networkStatus, serverStatus, maintenance } = useStatus();
  const location = useLocation();
  const guestAccessGranted = location.state?.guestAccessGranted === true;

  const maintenanceBlocksAccess = maintenance === true && !isSuperAdmin;

  const backendDown =
    networkStatus === 'offline'
    || serverStatus === 'unreachable'
    || maintenanceBlocksAccess;
  const {
    statusLoading,
    identityComplete,
    needsOnboardingRedirect,
    orgMembershipEligible,
    effectiveIdentity,
    onboardingCompletedAt,
    subjectOnboardingCompletedAt,
  } = useWebsiteAccessGate();

  useEffect(() => {
    if (!isLoggedIn && !isLoading && !sessionStorage.getItem('initialPath')) {
      const fullPath = location.pathname + location.search;
      sessionStorage.setItem('initialPath', fullPath);
    }
  }, [isLoggedIn, isLoading, location]);

  const onOnboardingSection =
    location.pathname === ONBOARDING_PREFIX || location.pathname.startsWith(`${ONBOARDING_PREFIX}/`);

  const needsOnboarding =
    needsOnboardingRedirect && !onOnboardingSection;

  // Guest just-approved: allow through while SecGroup Guest / isGuest catches up.
  const blockedWithoutOrgMembership =
    identityComplete && !orgMembershipEligible && !guestAccessGranted;

  if (
    isLoggedIn
    && !statusLoading
    && !securityLoading
    && !(isViewAsActive && viewAsLoading)
    && (needsOnboarding || !identityComplete || blockedWithoutOrgMembership)
  ) {
    logOnboardingGateDecision({
      label: blockedWithoutOrgMembership
        ? 'PrivateRoute → redirect /GamerIdentityCheck (org membership)'
        : needsOnboarding
          ? 'PrivateRoute → redirect /members/onboarding'
          : 'PrivateRoute → redirect /GamerIdentityCheck',
      identity: effectiveIdentity,
      onboardingCompletedAt,
      subjectOnboardingCompletedAt,
      isViewAsActive,
      isSuperAdmin,
      viewAsUserId,
      userId,
      pathname: location.pathname,
      statusLoading,
      securityLoading,
    });
  }

  if (isLoading || securityLoading || (isLoggedIn && (statusLoading || (isViewAsActive && viewAsLoading)))) {
    return (
      <div className="private-route-root">
        <LoadingSpinner />
      </div>
    );
  }

  if (backendDown) {
    return (
      <div className="private-route-root">
        <UnderMaintenance />
      </div>
    );
  }

  return (
    <div className="private-route-root">
      {!isLoggedIn ? (
        <Navigate to="/login" replace />
      ) : !identityComplete || blockedWithoutOrgMembership ? (
        <Navigate to="/GamerIdentityCheck" replace />
      ) : needsOnboarding ? (
        <Navigate to={ONBOARDING_PREFIX} replace />
      ) : (
        children
      )}
    </div>
  );
}

export default PrivateRoute;
